import { useState } from 'react';
import type { Metricas as DatosMetricas } from '@medchoice/shared';
import { consultorio } from '../../api/consultorio';
import { usePedido } from '../../api/usePedido';
import {
  Cargando, FalloDeCarga, GraficoAnillo, GraficoBarras, GraficoLinea, Kpi, NotaGrafico, PanelGrafico, Selector,
} from '../../ui';
import { SeccionPanel } from '../../ui/layout/PaginaPanel';
import { decimal, pesos, porcentaje } from '../../ui/format';

const PERIODOS = [
  { valor: '4', texto: 'Últimas 4 semanas' }, { valor: '8', texto: 'Últimas 8 semanas' }, { valor: '12', texto: 'Últimas 12 semanas' },
];

// Todo sale de los turnos guardados: si se cierra o se cancela un turno, los números cambian solos.
export function Metricas() {
  const [semanas, setSemanas] = useState(8);
  const pedido = usePedido(() => consultorio.metricas(semanas), [semanas]);
  const m = pedido.datos;

  return (
    <SeccionPanel titulo="Métricas"
      descripcion={m ? `Últimas ${m.semanas} semanas · ${m.total} turnos en el período` : undefined}
      acciones={
        <div className="accion-selector">
          <Selector aria-label="Período" value={String(semanas)} opciones={PERIODOS} onChange={(e) => setSemanas(Number(e.target.value))} />
        </div>
      }>
      {pedido.estado === 'cargando' && !m && <Cargando />}
      {pedido.estado === 'error' && <FalloDeCarga mensaje={pedido.error ?? ''} alReintentar={pedido.recargar} />}
      {m && <Tablero m={m} />}
    </SeccionPanel>
  );
}

function Tablero({ m }: { m: DatosMetricas }) {
  const serie = m.por_semana.map((s) => s.atendidos);
  const promedio = serie.length ? serie.reduce((a, b) => a + b, 0) / serie.length : 0;

  return (
    <div className="pila">
      <div className="kpis">
        <Kpi etiqueta="Turnos atendidos" valor={m.atendidos} icono="check-circle" pista={`${m.proximos} próximos agendados`} />
        <Kpi etiqueta="Ingresos" valor={pesos(m.ingresos)} icono="wallet" pista={`Ticket promedio ${pesos(m.ticket_promedio)}`} />
        <Kpi etiqueta="Ausentismo" valor={porcentaje(m.tasa_ausentismo)} icono="user-x"
          pista={`${m.ausentes} de ${m.atendidos + m.ausentes} turnos cerrados`} />
        <Kpi etiqueta="Perdido por ausencias" valor={pesos(m.perdido)} icono="trending-down"
          pista={`Recuperaste ${pesos(m.recuperado)} en señas`} />
      </div>
      <div className="kpis">
        <Kpi etiqueta="Ocupación de la agenda" valor={m.ocupacion === null ? 'Sin horario' : porcentaje(m.ocupacion)} icono="calendar-days"
          pista={m.ocupacion === null ? 'Cargá tu horario de atención para calcularla' : 'Horas reservadas sobre las que ofreciste'} />
        <Kpi etiqueta="Satisfacción" valor={m.satisfaccion.promedio === null ? 'Sin reseñas' : `${decimal(m.satisfaccion.promedio)} de 5`} icono="star"
          pista={m.satisfaccion.resenas === 1 ? '1 reseña de turnos del período' : `${m.satisfaccion.resenas} reseñas de turnos del período`} />
      </div>

      <div className="graficos">
        <PanelGrafico titulo="Turnos atendidos por semana" subtitulo="Cada punto es una semana cerrada">
          <GraficoLinea datos={serie} etiquetas={m.por_semana.map((s) => s.etiqueta)} promedio={promedio} />
          <NotaGrafico>La línea punteada es el promedio del período: {promedio.toLocaleString('es-AR', { maximumFractionDigits: 1 })} turnos por semana.</NotaGrafico>
        </PanelGrafico>
        <PanelGrafico titulo="Cómo terminaron los turnos" subtitulo="Solo turnos del período que ya ocurrieron">
          <GraficoAnillo centro={{ valor: m.atendidos + m.ausentes + m.cancelados, texto: 'turnos' }} partes={[
            { etiqueta: 'Atendidos', valor: m.atendidos, color: 'var(--success)' },
            { etiqueta: 'Cancelados', valor: m.cancelados, color: 'var(--ink-300)' },
            { etiqueta: 'No asistieron', valor: m.ausentes, color: 'var(--danger)' },
          ]} />
        </PanelGrafico>
        <PanelGrafico titulo="Ausencias por día de la semana" subtitulo="Porcentaje de turnos cerrados en los que el paciente faltó">
          <GraficoBarras color="var(--danger)" formatear={porcentaje}
            items={m.ausencias_por_dia.map((d) => ({ etiqueta: d.etiqueta, valor: d.porcentaje }))} />
          <NotaGrafico>Los días con más ausencias son los que más ganan con el recordatorio de 2 horas antes.</NotaGrafico>
        </PanelGrafico>
        <PanelGrafico titulo="Atendidos por servicio" subtitulo="Qué consultas se hacen más">
          <GraficoBarras items={m.por_servicio.map((s) => ({ etiqueta: s.etiqueta, valor: s.atendidos }))} />
        </PanelGrafico>
      </div>
    </div>
  );
}
